import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  deleteTipos,
  getTiposByOrgId,
  postTipo,
  putTipoById,
} from "../../../api/broker/tipos";
import { toast } from "react-toastify";
import {
  iDataTipo,
  TipoFields,
} from "../../../interfaces/SuperAdmin/Pages/iDataTipos";
import { NivelesFields } from "../../../interfaces/SuperAdmin/Pages/iDataNivel";

interface TiposContextProps {
  tipos: iDataTipo[];

  toEditTipo: iDataTipo | null;
  selectedTipos: string[];
  setSelectedTipos: Dispatch<SetStateAction<string[]>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;

  isEditTipoModalOpen: boolean;
  isCreateTipoModalOpen: boolean;
  isDeleteTipoModalOpen: boolean;

  isLoadingTipos: boolean;
  isEditingTipo: boolean;
  isCreatingTipo: boolean;
  isDeletingTipo: boolean;

  handleEditTipo: (tipo: iDataTipo) => void;
  toggleEditTipoModal: (isOpen: boolean) => void;
  toggleCreateTipoModal: (isOpen: boolean) => void;
  toggleDeleteTipoModal: (isOpen: boolean) => void;

  tipoEdit: (data: TipoFields, id_tipo: string) => Promise<void>;
  tipoCreate: (tipo: TipoFields, id_organizacion: string) => Promise<void>;
  tipoDelete: (body: { ids: string[] }) => Promise<void>;
}

const TiposContext = createContext<TiposContextProps>({} as TiposContextProps);

interface Props {
  children: React.ReactNode;
}

const TiposProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();

  const [tipos, setTipos] = useState<iDataTipo[]>([]);

  const [toEditTipo, setToEditTipo] = useState<iDataTipo | null>(null);
  const [selectedTipos, setSelectedTipos] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  const [isEditTipoModalOpen, setIsEditTipoModalOpen] = useState(false);
  const [isCreateTipoModalOpen, setIsCreateTipoModalOpen] = useState(false);
  const [isDeleteTipoModalOpen, setIsDeleteTipoModalOpen] = useState(false);

  const [isLoadingTipos, setIsLoadingTipos] = useState(true);
  const [isCreatingTipo, setIsCreatingTipo] = useState(false);
  const [isEditingTipo, setIsEditingTipo] = useState(false);
  const [isDeletingTipo, setIsDeletingTipo] = useState(false);

  // Función para extraer tipos
  const fetchTipos = async (id: string) => {
    setIsLoadingTipos(true);
    try {
      const tiposRes = await getTiposByOrgId(id);
      console.log(tiposRes);

      if (tiposRes.data.statusCode === 200) {
        setTipos(tiposRes.data.body);
      } else {
        toast.error("Error al obtener los tipos de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los tipos de la organización");
    } finally {
      setIsLoadingTipos(false);
    }
  };

  // Crear tipo
  const tipoCreate = async (tipo: TipoFields, id_organizacion: string) => {
    setIsCreatingTipo(true);
    try {
      //Creamos el tipo
      const tipoRes = await postTipo(tipo, id_organizacion);
      console.log(tipoRes);

      if (tipoRes.data.statusCode === 201) {
        setTipos((prev) => [...prev, tipoRes.data.body]);
        toggleCreateTipoModal(false);
        toast.success("Tipo creado exitosamente");
      } else {
        toast.error("Error al crear tipo en la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear tipo en la organización");
    } finally {
      setIsCreatingTipo(false);
    }
  };

  // Edicion de tipos
  const tipoEdit = async (data: TipoFields, id_tipo: string) => {
    setIsEditingTipo(true);
    try {
      const res = await putTipoById(data, id_tipo);
      console.log(res);
      if (res.data.statusCode === 200) {
        setTipos((prev) =>
          prev.map((tipo) => {
            if (tipo.id_tipo === id_tipo) {
              return res.data.body;
            }
            return tipo;
          })
        );
        toggleEditTipoModal(false);
        toast.success("Tipo editado exitosamente");
      } else {
        toast.error("Error al editar tipo");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar tipo");
    } finally {
      setIsEditingTipo(false);
    }
  };

  // Eliminar tipos
  const tipoDelete = async (body: { ids: string[] }) => {
    setIsDeletingTipo(true);
    try {
      const res = await deleteTipos(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedTipos([]);
        setTipos((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_tipo))
        );
        toggleDeleteTipoModal(false);
        toast.success("Tipos eliminados exitosamente");
      } else {
        toast.error("Error al eliminar los tipos de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los tipos de la organización");
    } finally {
      setIsDeletingTipo(false);
    }
  };

  // Abrir menu de editar tipo
  const toggleEditTipoModal = (isOpen: boolean) => {
    if (!isOpen) {
      setToEditTipo(null);
    }
    setIsEditTipoModalOpen(isOpen);
  };

  // Abrir menu de crear tipo
  const toggleCreateTipoModal = (isOpen: boolean) => {
    setIsCreateTipoModalOpen(isOpen);
  };

  // Abrir menu de eliminar tipos
  const toggleDeleteTipoModal = (isOpen: boolean) => {
    setIsDeleteTipoModalOpen(isOpen);
  };

  // Seleccionar tipo a editar
  const handleEditTipo = (tipo: iDataTipo) => {
    setToEditTipo(tipo);
    toggleEditTipoModal(true);
  };

  useEffect(() => {
    if (idOrganization) {
      fetchTipos(idOrganization);
    }
  }, [idOrganization]);

  return (
    <TiposContext.Provider
      value={{
        tipos,
        setSelectedTipos,
        toEditTipo,
        selectedTipos,
        searchText,
        setSearchText,
        isEditTipoModalOpen,
        isCreateTipoModalOpen,
        isDeleteTipoModalOpen,
        isLoadingTipos,
        isEditingTipo,
        isCreatingTipo,
        isDeletingTipo,
        handleEditTipo,
        toggleEditTipoModal,
        toggleCreateTipoModal,
        toggleDeleteTipoModal,
        tipoEdit,
        tipoCreate,
        tipoDelete,
      }}
    >
      {children}
    </TiposContext.Provider>
  );
};

const useTipos = () => useContext(TiposContext);

export { TiposProvider, useTipos };
